import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IPackage, IPackagePrice } from '@root/types/package';
import { ILoading } from '@root/types/common';
import { giftCardApi, packageApi } from '@root/api';
import { IRenewalOrderData } from '@root/types/order';
import Toast from '@root/components/toast';
import useBillAddress from '../useBillAddress';
import usePayMethod from '../usePayMethod';
import {
  EPayMode,
  payModeStringMap,
  UNLIMITED_PACKAGE_NUMBER,
} from '@root/constant';

const useGiftCardRenewal = () => {
  const { t } = useTranslation();
  const { packageId } = useParams();
  const payMethodState = usePayMethod();
  const { isBillAddress, onAddBill } = useBillAddress();
  const [packageDetail, setPackageDetail] = useState<IPackage>({} as IPackage);
  const [loading, setLoading] = useState<ILoading>(-1);
  const [paying, setPaying] = useState(false);
  const [selectPriceId, setSelectPriceId] = useState('');

  const { payMethod } = payMethodState;

  const onSelected = useCallback((checkedPriceId: string) => {
    setSelectPriceId(checkedPriceId);
  }, []);

  // 礼品卡购买的套餐续订
  const onCreateRenewal = useCallback(() => {
    if (!packageId || !selectPriceId) {
      return;
    }

    if (!isBillAddress) {
      onAddBill();
      return;
    }

    setPaying(true);
    Toast.info(t('create_order'), 0);

    const _baseUrl = `${window.location.origin}${process.env.REACT_APP_ROUTE_BASE_NAME}`;

    const _data: IRenewalOrderData = {
      priceId: selectPriceId,
      packageId,
      payMode: payModeStringMap[payMethod] || payModeStringMap[EPayMode.PayPal],
      cancelUrl: `${_baseUrl}/`,
      errorUrl: `${_baseUrl}/payFailed`,
      successUrl: `${_baseUrl}/paySuccess`,
    };

    packageApi
      .packageOrderRenewal(_data)
      .then((data: any) => {
        Toast.destroy();
        setPaying(false);
        // Toast.info(t('save_successful'));
        window.location.href = data?.url || _data.cancelUrl;
      })
      .catch(() => {
        setPaying(false);
        Toast.destroy();
        Toast.info(t('save_failed'));
      });
  }, [packageId, isBillAddress, t, payMethod, onAddBill, selectPriceId]);

  const getPackage = useCallback(() => {
    if (!packageId) {
      return;
    }

    setLoading(0);

    giftCardApi
      .getGoodsInfoByPackageId(packageId)
      .then((data: any) => {
        // 过滤掉无限套餐价钱
        const _goodsPriceList = (data?.goodsPriceList || []).filter(
          (i: IPackagePrice) =>
            i.planUnit === 'month' && i.buyNum !== UNLIMITED_PACKAGE_NUMBER,
        );
        // 默认选中第一个价钱
        if (_goodsPriceList.length) {
          setSelectPriceId(_goodsPriceList[0].goodsPriceId);
        }

        setPackageDetail(data || {});
        setLoading(1);
      })
      .catch(() => {
        setLoading(2);
      });
  }, [packageId]);

  useEffect(() => {
    getPackage();
  }, [getPackage]);

  useEffect(() => {
    return () => {
      Toast.destroy();
    };
  }, []);

  return {
    state: {
      packageDetail,
      loading,
      selectPriceId,
      paying,
      payMethodState,
    },
    action: {
      onCreateRenewal,
      onSelected,
      getPackage,
    },
  };
};

export default useGiftCardRenewal;
